import { useState } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import {
  Box,
  Button,
  Card,
  Heading,
  Image,
  NotificationBox,
  RadioGroup,
  RadioTile,
  Stack,
  Stretch,
  Text,
} from '@eneco/sparky';
import { Electricity, ElectricityAndGas, Gas } from '@eneco/sparky/illustrations';
import { NotificationBarProps } from '@eneco/sparky/types';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { PriceSimulationRenderingExtended } from '@sitecore/types/manual/be/PriceSimulationExtended';
import { Controller, useFormContext } from 'react-hook-form';

import { PriceSimulationFormSchema } from '../schemas/priceSimulationFormSchema';
import { FlowChildProps } from '../types/PriceSimulationSteps';
import { getTrackableFormValues } from '../utils/getTrackableFormValues';

export const EnergyTypeStep = ({ nextMainStep }: FlowChildProps) => {
  const { fields } = useContent<PriceSimulationRenderingExtended>();
  const { control, getValues } = useFormContext<PriceSimulationFormSchema>();
  const [error, setError] = useState<NotificationBarProps | null>(null);
  const { trackEventBE } = useTracking();

  const onNextClick = () => {
    if (getValues('energyType') === undefined) {
      setError({
        variant: 'error',
        title: fields.energyTypeSelectionStep.errorNotification.value.title,
        text: fields.energyTypeSelectionStep.errorNotification.value.content,
      });
      return;
    }
    trackEventBE('pbt_calculation_energy_type', {
      status: 'click',
      data: getTrackableFormValues(getValues()),
    });
    nextMainStep();
  };

  const hasPromoContentInSitecore =
    fields.energyTypeSelectionStep.promoDiscountImage.value.formats[0].src ||
    fields.energyTypeSelectionStep.promoDiscountNotification.value.title ||
    fields.energyTypeSelectionStep.promoDiscountNotification.value.content;

  return (
    <>
      <Stack gap="2">
        <Heading as="h1" size="M">
          {fields.energyTypeSelectionStep.title.value}
        </Heading>
        <Text>{fields.energyTypeSelectionStep.subTitle.value}</Text>
      </Stack>

      {hasPromoContentInSitecore && (
        <Card>
          <Box padding="6">
            <Stack direction="row" gap="6">
              <Image
                src={fields.energyTypeSelectionStep.promoDiscountImage.value.formats[0].src}
                alt={fields.energyTypeSelectionStep.promoDiscountImage.value.alt}
                width="160px"
                height="160px"
                objectFit="contain"
              />
              <Stack gap="4">
                <Heading as="h2" size="S">
                  {fields.energyTypeSelectionStep.promoDiscountNotification.value.title}
                </Heading>
                <RichText html={fields.energyTypeSelectionStep.promoDiscountNotification.value.content} />
              </Stack>
            </Stack>
          </Box>
        </Card>
      )}

      <Stretch>
        <div>
          <Controller
            control={control}
            name={'energyType'}
            render={({ field: { onChange, value } }) => (
              <RadioGroup
                name={'energyType'}
                aria-labelledby={fields.energyTypeSelectionStep.energyTypesList.value.label}
                direction="column"
                value={value?.toString()}
                onValueChange={onChange}>
                <RadioTile value={'Both'}>
                  <Stack direction="row" gap="2" alignY="center">
                    <ElectricityAndGas size="small" />
                    <Text>
                      {
                        fields.energyTypeSelectionStep.energyTypesList.value.options.find(
                          option => option.name === 'Both',
                        )?.label
                      }
                    </Text>
                  </Stack>
                </RadioTile>
                <RadioTile value={'Electricity'}>
                  <Stack direction="row" gap="2" alignY="center">
                    <Electricity size="small" />
                    <Text>
                      {
                        fields.energyTypeSelectionStep.energyTypesList.value.options.find(
                          option => option.name === 'Electricity',
                        )?.label
                      }
                    </Text>
                  </Stack>
                </RadioTile>
                <RadioTile value={'Gas'}>
                  <Stack direction="row" gap="2" alignY="center">
                    <Gas size="small" />
                    <Text>
                      {
                        fields.energyTypeSelectionStep.energyTypesList.value.options.find(
                          option => option.name === 'Gas',
                        )?.label
                      }
                    </Text>
                  </Stack>
                </RadioTile>
              </RadioGroup>
            )}
          />
        </div>
      </Stretch>
      {error && <NotificationBox variant="error" isAlert {...error} />}

      <Button onClick={onNextClick}>{fields.navigation.nextButtonText.value}</Button>
      <Text size="BodyS" color="textLowEmphasis">
        <RichText html={fields.energyTypeSelectionStep.disclaimerContent.value} />
      </Text>
    </>
  );
};
